import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GameFrame, BetControls } from "@/components/GameFrame";
import { useAudio } from "@/context/GameFeel";

const WEDGES = [0, 1.5, 0, 2, 0, 3, 0, 1.5, 0, 5, 0, 2, 0, 1.5, 0, 10];
const COLORS = ["#1f2937", "#F59E0B", "#1f2937", "#8B5CF6", "#1f2937", "#EC4899", "#1f2937", "#F59E0B", "#1f2937", "#10B981", "#1f2937", "#8B5CF6", "#1f2937", "#F59E0B", "#1f2937", "#06B6D4"];

export default function WheelGame() {
  const { user, setUser } = useAuth();
  const { react, sfx } = useAudio();
  const [bet, setBet] = useState(100);
  const [rot, setRot] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const seg = 360 / WEDGES.length;

  const spin = async () => {
    if (bet > (user?.coins ?? 0)) return toast.error("Not enough coins");
    setSpinning(true);
    try {
      const { data } = await api.post("/games/wheel/spin", { bet });
      const target = data.wedge_index;
      const finalRot = 360 * 6 + (360 - target * seg - seg / 2);
      setRot((prev) => prev - (prev % 360) + finalRot);
      const ticks = setInterval(() => sfx("tick"), 180);
      await new Promise((r) => setTimeout(r, 4200));
      clearInterval(ticks);
      setUser((u) => ({ ...u, coins: data.coins }));
      if (data.payout > 0) toast.success(`${data.multiplier}x · +${data.payout.toLocaleString()} coins 🎉`);
      else toast("No luck — spin again!");
      react({ outcome: data.payout > 0 ? "win" : "loss", game: "wheel", big: data.multiplier >= 5 });
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSpinning(false);
    }
  };

  const gradient = WEDGES.map((_, i) => `${COLORS[i]} ${i * seg}deg ${(i + 1) * seg}deg`).join(", ");

  return (
    <GameFrame title="Fortune Wheel" subtitle="Land a wedge for up to 10x"
      side={<BetControls bet={bet} setBet={setBet} coins={user?.coins} disabled={spinning} onPlay={spin} playLabel={spinning ? "Spinning…" : "SPIN WHEEL"} testid="wheel-spin" />}>
      <div className="glass rounded-3xl p-8 border border-amber-500/20 glow-coins flex flex-col items-center">
        <div className="relative">
          <div className="absolute left-1/2 -translate-x-1/2 -top-2 z-20 w-0 h-0 border-l-[12px] border-r-[12px] border-t-[20px] border-l-transparent border-r-transparent border-t-amber-400" />
          <motion.div animate={{ rotate: rot }} transition={{ duration: 4, ease: [0.16, 1, 0.3, 1] }}
            className="w-72 h-72 sm:w-80 sm:h-80 rounded-full border-4 border-amber-500/40 relative"
            style={{ background: `conic-gradient(${gradient})` }}>
            {WEDGES.map((m, i) => (
              <div key={i} className="absolute inset-0 flex items-start justify-center" style={{ transform: `rotate(${i * seg + seg / 2}deg)` }}>
                <span className="mt-3 text-xs font-mono font-bold text-white/90">{m}x</span>
              </div>
            ))}
            <div className="absolute inset-0 m-auto w-14 h-14 rounded-full bg-black border-2 border-amber-500/50" style={{ top: 0, bottom: 0, left: 0, right: 0 }} />
          </motion.div>
        </div>
      </div>
    </GameFrame>
  );
}
